import { useCallback, useEffect, useMemo } from 'react';

import { DEFAULT_EDGE_PADDING } from '../../constants';
import { getPinsBounds } from '../../helpers';
import { log, withSinglePinBoundsOffsetsCoordinates } from '../../utils';
import { useNotMinimizedPins } from '../useNotMinimizedPins';
import { type Params } from './useMapAutoBounds.types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Automatically fits the map to the bounds of the pins.
 */
export const useMapAutoBounds = (props: Params) => {
  const { isEnabled, mapRef, edgePadding, pins, isOffsetEnabled } = props;

  const filteredPins = useNotMinimizedPins(pins);

  // ─────────────────────────────────────────────────────────────────────

  const mapPadding = useMemo(() => {
    if (!isOffsetEnabled) return 0;

    return {
      top: edgePadding?.top ?? DEFAULT_EDGE_PADDING,
      right: edgePadding?.right ?? DEFAULT_EDGE_PADDING,
      bottom: edgePadding?.bottom ?? DEFAULT_EDGE_PADDING,
      left: edgePadding?.left ?? DEFAULT_EDGE_PADDING,
    };
  }, [isOffsetEnabled, edgePadding]);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const fitBoundsOnIdle = useCallback(
    (bounds: google.maps.LatLngBounds) => {
      requestIdleCallback?.(() => {
        mapRef?.fitBounds(bounds, mapPadding);
      });
    },
    [mapRef, mapPadding],
  );

  const fitBounds = useCallback(() => {
    if (!mapRef) {
      log.error('`map` object was not found!');

      return;
    }

    const pinsBounds = getPinsBounds(withSinglePinBoundsOffsetsCoordinates(filteredPins)); // prettier-ignore

    if (!pinsBounds || pinsBounds.isEmpty()) return;

    fitBoundsOnIdle(pinsBounds);
  }, [mapRef, filteredPins, fitBoundsOnIdle]);

  const fitBoundsOnChange = useCallback(() => {
    if (!isEnabled) return;

    if (!mapRef) {
      log.error('`map` object was not found!');

      return;
    }

    try {
      fitBounds();
    } catch (error) {
      log.error('Error fitting bounds:', error);
    }
  }, [fitBounds, isEnabled, mapRef]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(fitBoundsOnChange, [fitBoundsOnChange]);
};
